/*
  Soup Latte "Daisy" hero animation. Adapted from the Soup Latte handoff
  (daisy-animation/index.html). The SVG (assets/daisy-layered.svg) is too
  big for a Liquid file, so it is fetched and put inline here: it has to be
  inline for its parts (vegetables, bubbles, steam) to animate.

  Sequence: red background -> dots spread from the center -> Daisy pops in
  small and grows to full size (the veggies in her crown stay still) ->
  meanwhile copies of the veggies roll in from the left and around the
  hero -> once she is full size they fly off in different directions ->
  bubbles pop in -> steam curls up from the cup on a loop.
*/
(function () {
  if (window.__daisyAnimLoaded) return;
  window.__daisyAnimLoaded = true;

  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var svgCache = {};

  function loadSvg(url) {
    if (!svgCache[url]) {
      svgCache[url] = fetch(url, { credentials: 'same-origin' }).then(function (r) {
        if (!r.ok) throw new Error('daisy svg ' + r.status);
        return r.text();
      });
    }
    return svgCache[url];
  }

  /*
    Keep the hero copy from covering Daisy: measure the space the text
    leaves (beside it on desktop, below it when stacked on phones and
    upright tablets) and cap her height to fit (--daisy-max-h). On
    narrower desktops she shrinks and moves down to the hero's bottom edge.
  */
  var VB_W = 860.98, VB_H = 1175.26; // the SVG's viewBox
  var TOP_Y = 20;                    // her crown top, SVG units
  var GAP = 16;

  // Left edge of her art (SVG x) for each 50-unit band down the SVG,
  // traced from daisy-layered.svg: bubble, cup, arm, ...
  var CONTOUR = [320, 244, 234, 210, 152, 66, 60, 88, 136, 114, 112, 114, 106, 96, 82, 66, 46, 36, 34, 36, 48, 78, 204, 282];
  var BAND = 50;
  var CUT_Y = 960; // when she sits low, the SVG is cut here (below her scarf)

  // Line boxes of the hero copy (the text's own lines, not full-width blocks)
  function textRects(root) {
    var inner = root.parentElement && root.parentElement.querySelector('.hero__inner');
    var out = [];
    if (!inner) return out;
    inner.querySelectorAll('img, h1, h2, h3, p, .button, a').forEach(function (el) {
      var list;
      if (el.tagName === 'IMG' || el.classList.contains('button')) {
        list = [el.getBoundingClientRect()];
      } else {
        var range = document.createRange();
        range.selectNodeContents(el);
        list = Array.prototype.slice.call(range.getClientRects());
      }
      list.forEach(function (r) {
        if (r.width && r.height) out.push({ left: r.left, right: r.right, top: r.top, bottom: r.bottom });
      });
    });
    return out;
  }

  // Does her art (scale s, SVG top-right corner at right/top) touch the copy?
  function hits(rects, s, right, top) {
    for (var i = 0; i < CONTOUR.length; i++) {
      var y1 = top + i * BAND * s, y2 = y1 + BAND * s;
      var x = right - (VB_W - CONTOUR[i]) * s;
      for (var j = 0; j < rects.length; j++) {
        var t = rects[j];
        if (t.right + GAP > x && t.bottom + GAP > y1 && t.top - GAP < y2) return true;
      }
    }
    return false;
  }

  function fit(root) {
    var stage = root.querySelector('.daisy-stage');
    if (!stage) return;
    root.style.removeProperty('--daisy-max-h');
    root.style.removeProperty('--daisy-top-px');
    root.classList.remove('is-low');
    var rects = textRects(root);
    if (!rects.length) return;
    var hero = root.getBoundingClientRect();
    var st = stage.getBoundingClientRect();
    if (!st.height || !st.width) return;
    var stacked = window.matchMedia('(max-width: 749px), (max-width: 1199px) and (orientation: portrait)').matches;
    var s = Math.min(st.width / VB_W, st.height / VB_H);
    var t = rects.reduce(function (a, r) { return Math.max(a, r.bottom); }, 0);
    if (stacked) {
      // drawing sits on the stage's bottom edge; crown must clear the text
      var crown = st.bottom - VB_H * s + TOP_Y * s;
      if (crown >= t + GAP) return;
      var sMax = (st.bottom - t - GAP) / (VB_H - TOP_Y);
      root.style.setProperty('--daisy-max-h', Math.floor(Math.max(sMax * VB_H, hero.height * 0.3)) + 'px');
      return;
    }
    // Desktop: full size with her crown at the top, if the copy leaves room
    if (!hits(rects, s, st.right, st.bottom - VB_H * s)) return;
    // Otherwise she shrinks until the copy clears her - but stays at least
    // half the hero tall. As she shrinks she drops to sit on the hero's
    // bottom edge (never showing below her scarf).
    var sMin = hero.height * 0.5 / (CUT_Y - TOP_Y);
    var topFor = function (k) { return Math.max(st.top, hero.bottom - CUT_Y * k); };
    var sFit = s;
    while (sFit > sMin && hits(rects, sFit, st.right, topFor(sFit))) sFit *= 0.97;
    sFit = Math.max(sFit, sMin);
    root.classList.add('is-low');
    root.style.setProperty('--daisy-max-h', Math.floor(sFit * VB_H) + 'px');
    root.style.setProperty('--daisy-top-px', Math.round(topFor(sFit) - hero.top) + 'px');
  }

  var fitTimer;
  window.addEventListener('resize', function () {
    clearTimeout(fitTimer);
    fitTimer = setTimeout(function () {
      document.querySelectorAll('.daisy-anim').forEach(function (r) {
        if (r.__daisyFit) r.__daisyFit();
      });
    }, 150);
  });

  function setup(root) {
    if (root.__daisy) return;
    root.__daisy = true;
    var stage = root.querySelector('.daisy-stage');
    var url = stage && stage.dataset.svgSrc;
    if (!url) return;

    loadSvg(url)
      .then(function (markup) {
        stage.innerHTML = markup;
        // Keep her standing on the bottom edge when the box is narrower
        var s = stage.querySelector('svg');
        if (s) s.setAttribute('preserveAspectRatio', 'xMidYMax meet');
        root.__daisyFit = function () {
          fit(root);
          document.dispatchEvent(new CustomEvent('steam:relayout'));
        };
        fit(root);
        // web fonts can change the copy's size after load
        if (document.fonts && document.fonts.ready) document.fonts.ready.then(root.__daisyFit);
        start(root);
      })
      .catch(function () {
        // Show the background at least
        root.classList.add('is-ready');
      });
  }

  function start(root) {
    var svg = root.querySelector('svg');
    var bg = root.querySelector('.daisy-bg');
    var dots = root.querySelector('.daisy-dots');
    // Veggies that roll around the hero (copies of the ones in her crown,
    // which itself stays still)
    var ROLLERS = ['#veg-squash', '#veg-beet', '#veg-broccoli', '#veg-mushroom', '#veg-coconut'];
    var running = [];
    var layer = null;

    function rand(a, b) { return a + Math.random() * (b - a); }

    // A free-standing copy of one veggie, sized like the one in her crown
    function makeRoller(sel, scale) {
      var src = svg.querySelector(sel);
      if (!src) return null;
      var b = src.getBBox();
      var NS = 'http://www.w3.org/2000/svg';
      var el = document.createElementNS(NS, 'svg');
      el.setAttribute('class', 'daisy-svg daisy-roller');
      el.setAttribute('viewBox', b.x + ' ' + b.y + ' ' + b.width + ' ' + b.height);
      el.setAttribute('aria-hidden', 'true');
      var g = src.cloneNode(true);
      g.removeAttribute('id');
      g.removeAttribute('class');
      el.appendChild(g);
      var w = Math.max(34, b.width * scale), h = w * b.height / b.width;
      el.style.width = w + 'px';
      el.style.height = h + 'px';
      layer.appendChild(el);
      return { el: el, w: w, h: h };
    }

    function at(r, x, y, deg, extra) {
      return 'translate(' + (x - r.w / 2) + 'px,' + (y - r.h / 2) + 'px) rotate(' + deg + 'deg)' + (extra || '');
    }

    function play() {
      running.forEach(function (a) { a.cancel(); });
      running = [];
      if (layer) layer.remove();
      layer = null;
      root.classList.remove('is-steaming');
      root.classList.add('is-ready');
      if (reduce || !svg.animate) {
        if (root.querySelector('.steam-layer')) root.classList.add('is-steaming');
        return;
      }

      // 0) Red background fades in, then the dots spread out from the center
      running.push(bg.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 500, easing: 'ease-out', fill: 'both' }));
      running.push(dots.animate([
        { clipPath: 'circle(0% at 60% 45%)', opacity: 0, transform: 'scale(1.08)' },
        { opacity: 1, offset: 0.3 },
        { clipPath: 'circle(90% at 60% 45%)', opacity: 1, transform: 'scale(1)' }
      ], { duration: 1100, delay: 350, easing: 'cubic-bezier(.3,.6,.2,1)', fill: 'both' }));

      // Measure her full size before she starts animating
      var rect = svg.getBoundingClientRect();
      var box = root.getBoundingClientRect();
      var scale = Math.min(rect.width / VB_W, rect.height / VB_H);
      var W = box.width, H = box.height;

      // 1) Daisy pops in small, then grows to full size (her crown comes along, still)
      var POP = 900;    // when she pops in (ms)
      var GROW = 3000;  // pop + grow
      var FULL = POP + GROW;
      running.push(svg.animate([
        { transform: 'scale(.12)', opacity: 0, easing: 'cubic-bezier(.3,1.7,.5,1)' },
        { transform: 'scale(.42)', opacity: 1, offset: 0.12, easing: 'ease-in-out' },
        { transform: 'scale(.4)', offset: 0.2, easing: 'cubic-bezier(.45,0,.4,1)' },
        { transform: 'scale(1.03)', offset: 0.9, easing: 'ease-out' },
        { transform: 'scale(1)', opacity: 1 }
      ], { duration: GROW, delay: POP, fill: 'both' }));
      svg.style.transformOrigin = '55% 40%';

      // 2) Veggies roll in from the left and around the hero while she grows,
      // 3) then fly off in different directions once she is full size
      layer = document.createElement('div');
      layer.className = 'daisy-rollers';
      layer.setAttribute('aria-hidden', 'true');
      root.appendChild(layer);
      var exits = [-150, -100, -45, 10, 60, 115, 165].sort(function () { return Math.random() - 0.5; });
      ROLLERS.forEach(function (sel, i) {
        var r = makeRoller(sel, scale);
        if (!r) return;
        var startAt = POP + 150 + i * 200;
        // Path: enter from the left edge, then wander to a few spots
        var pts = [{ x: -r.w * 0.7, y: H * rand(0.2, 0.85) }];
        for (var k = 0; k < 3; k++) pts.push({ x: W * rand(0.08 + k * 0.12, 0.45 + k * 0.15), y: H * rand(0.12, 0.88) });
        // Rolling: turn by distance / radius, clockwise when moving right
        var radius = Math.max(r.w, r.h) / 2;
        var deg = rand(-40, 40), dist = 0, lens = [0];
        for (k = 1; k < pts.length; k++) {
          var dx = pts[k].x - pts[k - 1].x, dy = pts[k].y - pts[k - 1].y;
          var d = Math.sqrt(dx * dx + dy * dy);
          dist += d;
          lens.push(dist);
          pts[k].deg = (pts[k - 1].deg === undefined ? deg : pts[k - 1].deg) + (dx >= 0 ? 1 : -1) * d / radius * 57.3;
        }
        pts[0].deg = deg;
        var frames = pts.map(function (p, k) {
          return { transform: at(r, p.x, p.y, p.deg), offset: lens[k] / dist };
        });
        frames[0].opacity = 1;
        running.push(r.el.animate(frames, { duration: FULL - startAt, delay: startAt, easing: 'cubic-bezier(.35,.1,.55,.95)', fill: 'both' }));

        var last = pts[pts.length - 1];
        var a = exits[i % exits.length] * Math.PI / 180;
        var far = Math.sqrt(W * W + H * H) * 0.8;
        var spin = (Math.cos(a) >= 0 ? 1 : -1) * rand(240, 420);
        var exit = r.el.animate([
          { transform: at(r, last.x, last.y, last.deg, ' scale(1)'), opacity: 1 },
          { transform: at(r, last.x, last.y, last.deg + spin * 0.1, ' scale(1.15)'), opacity: 1, offset: 0.15 },
          { transform: at(r, last.x + Math.cos(a) * far, last.y + Math.sin(a) * far, last.deg + spin, ' scale(.7)'), opacity: 0 }
        ], { duration: 950, delay: FULL + i * 70, easing: 'cubic-bezier(.5,0,.85,.6)', fill: 'forwards' });
        running.push(exit);
      });
      var thisLayer = layer;
      setTimeout(function () { thisLayer.remove(); }, FULL + ROLLERS.length * 70 + 1200);

      // 3b) Steam from the cup. Canvas steam (steam-effect.js) if the
      // section has it on, otherwise the SVG's own line wisps
      if (root.querySelector('.steam-layer')) {
        setTimeout(function () {
          root.classList.add('is-steaming');
          document.dispatchEvent(new CustomEvent('steam:relayout'));
        }, FULL + 200);
      }
      if (!root.querySelector('.steam-layer')) Array.prototype.slice.call(root.querySelectorAll('.wisp')).forEach(function (w, i) {
        running.push(w.animate([
          { strokeDashoffset: 100, opacity: 0, transform: 'translateY(6px) scaleX(1)' },
          { strokeDashoffset: 40, opacity: 0.95, offset: 0.35 },
          { strokeDashoffset: 0, opacity: 0.75, offset: 0.65 },
          { strokeDashoffset: -60, opacity: 0, transform: 'translateY(-34px) scaleX(' + (i % 2 ? 1.25 : 0.8) + ')' }
        ], { duration: 3000 + i * 300, delay: FULL + i * 700, iterations: Infinity, easing: 'ease-in-out' }));
      });

      // 4) Speech bubbles pop in one by one: small dot, medium dot, big "I'm a Bit Salty"
      var BUBBLES_AT = FULL + 300;
      ['#bubble-1', '#bubble-2', '#bubble-3'].forEach(function (sel, i) {
        var b = root.querySelector(sel);
        if (!b) return;
        running.push(b.animate([
          { transform: 'scale(0)', opacity: 0 },
          { transform: 'scale(1.18)', opacity: 1, offset: 0.6 },
          { transform: 'scale(.95)', offset: 0.82 },
          { transform: 'scale(1)', opacity: 1 }
        ], { duration: i === 2 ? 600 : 360, delay: BUBBLES_AT + i * 260, easing: 'ease-out', fill: 'both' }));
      });
    }

    // Pause the endless loops while the hero is off screen; play the intro once
    var played = false;
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        var on = entries[0].isIntersecting;
        if (on && !played) {
          played = true;
          play();
        } else if (played) {
          running.forEach(function (a) {
            if (a.effect && a.effect.getTiming().iterations === Infinity) on ? a.play() : a.pause();
          });
        }
      }, { threshold: 0.2 }).observe(root);
    } else {
      play();
    }

    // Theme editor: replay when the section is selected
    root.__daisyPlay = play;
  }

  function initAll() {
    document.querySelectorAll('.daisy-anim').forEach(setup);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();
  document.addEventListener('shopify:section:load', initAll);
  document.addEventListener('shopify:section:select', function (e) {
    var r = e.target.querySelector('.daisy-anim');
    if (r && r.__daisyPlay) r.__daisyPlay();
  });
})();
